/**
 * One shared teardown for the client paths that END a session while the app
 * keeps running.
 *
 * The stop this repo issues is addressed to a SESSION, never to a RECORDING:
 * every stop is `stopSession()` with no identity, which Rust resolves through
 * `RecordingLifecycle::current_identity()`. Several client paths end a session
 * without issuing any native stop at all — "End session" while paused, each of
 * the three session creators WHEN IT NAVIGATES AWAY, and logout — so native
 * capture and chunk upload keep running against a session the clinician has
 * finished with. That is the cross-patient PHI exposure this module exists to
 * close (spec 012, FR-038).
 *
 * The "when it navigates away" half is not a detail, and `navigate: false` does
 * NOT mean the clinician stays where they were. It means the CALLER does the
 * navigating: six of the seven live consumers push to the session they just
 * minted on the next line. Those six therefore DO have this requirement's
 * shape, and the gate the two creators apply excludes them — a pre-existing gap
 * this change does not close. They are named, each with the line where it
 * pushes, under "Not closed by FR-038" in
 * `specs/012-app-8723-self-healing-stop/spec.md`. Do not read the gate as
 * evidence those paths were found safe; read the list.
 *
 * The seventh, `WorkSessionsSection.tsx`, is the only one that genuinely stays
 * put: its `onSuccess` opens the new session as an in-page artifact panel
 * (`setOpenArtifact`) and the file imports no router at all.
 *
 * The same half is why every creator calls this on its SUCCESS path, after the
 * create has returned and on the last awaited line before the redirect. The
 * governing property is: never stop capture unless the session it belongs to has
 * ACTUALLY ENDED — not when a caller is about to end it, and not when a prop says
 * it intends to navigate. A failed create does not navigate, so a sweep placed
 * before it ends capture on a consultation the clinician is still in.
 *
 * The shape to look for when adding a call site is **a client path that mints or
 * ends a session and navigates away** — not a fixed list of files. Which paths
 * have that shape today, and which were examined and deliberately left out (with
 * the line that establishes why), is recorded in FR-038 and in "Not closed by
 * FR-038" in `specs/012-app-8723-self-healing-stop/spec.md`. Keep that list
 * honest rather than claiming completeness here: a sentence in a comment saying
 * every entry point is covered is how the gap this module closes survived three
 * prior tickets.
 *
 * It is a plain async function rather than a hook on purpose: of its six call
 * sites across five files, four are React mutations and two
 * (`logout` and `logoutWithoutRedirect`, both in `src/lib/authActions.ts`) are
 * not in a component tree at all. It lives next to the bridge it wraps, which no
 * mutation hook and no auth module is imported by, so no call site creates an
 * import cycle.
 *
 * It probes, targets, stops and logs — or stands aside and logs that instead —
 * and deliberately does none of the finalization work the real stop path owns:
 * no note generation, no duration persistence, no UI or store writes, and no
 * `runRecorderSafetyStop` (that guard returns early on `timerModel.isRunning`
 * and would buy nothing here).
 */
import {
  onlineChunkUploadBridge,
  type ActiveOnlineChunkUpload,
} from '@/tauri/online-chunk-upload/session';
import { isTauri } from '@/tauri/utils';
import type { StopReason } from '@/types/self-healing-stop';
import { logger } from '@/utils/logger/logger';

/**
 * How long the sweep may hold a session end while it waits on native.
 *
 * Matched to `LEGACY_SWEEP_TIMEOUT_MS` (10_000) in
 * `src/hooks/mutation/useMutateStopRecording.ts` — the repo's existing bound on
 * `pTimeout(onlineChunkUploadBridge.stopSession(), …)` used as a best-effort
 * cross-engine sweep, documented there as "a hang must not stall the stop". Same
 * IPC, same path, same character, so the figure is inherited rather than chosen.
 *
 * It is NOT `STOP_SESSION_FIRST_ATTEMPT_TIMEOUT_MS` (900_000). That budget
 * covers drain + backup extract/upload + `:finish` on a stop the clinician is
 * already watching a spinner for; nobody can be made to wait it out merely to
 * end a session. The difference is safe because this bound limits how long the
 * CLIENT waits, not the native stop: the IPC is not cancelled, Rust keeps
 * converging past it, and a timeout is therefore reported as "not confirmed" —
 * never as "abandoned".
 *
 * The whole sweep shares this one deadline, so a wedged probe cannot spend it
 * and then let the stop spend it again.
 */
export const SESSION_END_SWEEP_TIMEOUT_MS = 10000;

/** Which path ended the session. Fixed set — it is a log dimension, not free text. */
export type SessionEndSweepCallSite =
  | 'endSessionFromPause'
  | 'createNewSession'
  | 'createNewSessionForOtherUser'
  | 'createOfflineSession'
  | 'logout'
  | 'logoutWithoutRedirect';

/**
 * Whose recording this caller may stop. Passed EXPLICITLY by the caller so the
 * decision is readable at the call site rather than buried in here.
 *
 * - `onlySession` — act only when native names this exact session. Used where
 *   another session's live recording belongs to another PATIENT, so stopping it
 *   would be a new harm rather than a missed fix.
 * - `anyActiveUpload` — act on whatever native names. Used where the user's
 *   involvement with any live recording is ending.
 */
export type SessionEndSweepTarget =
  | { kind: 'onlySession'; sessionId: string }
  | { kind: 'anyActiveUpload' };

/**
 * WHAT THE PROBE HAD ESTABLISHED when the stop IPC went out.
 *
 * - `probed` — `getActive()` answered, said `active`, and the targeting rule
 *   allowed the stop. The sweep knew something was live before acting.
 * - `unprobed` — `getActive()` never gave a usable answer and the target was
 *   `anyActiveUpload`, so the stop was issued blind. See
 *   {@link sweepCaptureOnSessionEnd} for why that is safe, and why it is
 *   `anyActiveUpload` only.
 *
 * It is a separate field from {@link SessionEndSweepStopOutcome} because the two
 * answer different questions — "what did we know?" and "what happened?" — and
 * collapsing them is what would make a blind stop that succeeded read in the log
 * exactly like a probed one, which is the misreport this module exists to avoid.
 */
export type SessionEndSweepStopBasis = 'probed' | 'unprobed';

/**
 * What the stop IPC established. One enum, because these four are mutually
 * exclusive answers to one question and a boolean cannot hold four values.
 *
 * - `stopped` — native confirmed the terminal **of the stop THIS sweep issued**.
 *   Proved by the returned `reason`, not by the call resolving `Ok`; see
 *   `nothingCurrent` below for why `Ok` alone is not that proof.
 * - `nothingCurrent` — **this sweep stopped nothing.** That is the member's
 *   meaning, and two different native answers deliver it:
 *
 *     1. Native answered `SELF_HEALING_STALE_EPOCH`. No epoch was ever
 *        installed, or a DIFFERENT epoch owns the pipeline. The stop returns
 *        before `terminal::execute` and touches nothing. Logged with
 *        `nothingCurrentVia: 'staleEpoch'`.
 *     2. Native answered **`Ok` with a terminal outcome that is not ours** —
 *        an idempotent replay. `terminal::execute` step 1
 *        (`src-tauri/src/recording_lifecycle/terminal.rs`) returns
 *        `Ok(cached_outcome)` for an already-terminal epoch, re-running no
 *        pipelines and stopping nothing; the test
 *        `execute_idempotent_replays_cached_outcome_without_touching_pipelines`
 *        in that file pins it. Because nothing ever clears the epoch
 *        (`mark_terminal` moves the phase only, `current_identity()` has no
 *        phase filter), a blind stop AFTER ANY COMPLETED RECORDING in the same
 *        app run lands on that terminal epoch and replays it. Logged with
 *        `nothingCurrentVia: 'unattributedTerminal'`.
 *
 *   **A prior revision of this file said `SELF_HEALING_STALE_EPOCH` also comes
 *   back for an already-terminal epoch. It does not** — that path returns `Ok`,
 *   which this helper used to classify as `stopped`: a cannot-tell answer read
 *   as a positive one, on the most ordinary sequence there is (record, stop,
 *   log out).
 *
 *   **This is not a failure**, and giving it its own member rather than folding
 *   it into `error` is the whole point: "there was nothing to stop" and "native
 *   refused to stop something live" are opposite outcomes, one routine and one
 *   an incident, and an alarm that cannot tell them apart teaches whoever reads
 *   it to ignore both.
 * - `notConfirmed` — the bound expired. Deliberately NOT called `'timeout'`: the
 *   IPC is not cancelled and Rust keeps converging past the bound, so what
 *   expired is the client's patience, not the stop. The name is the contract — a
 *   caller may conclude "not confirmed within the bound", never "abandoned".
 * - `error` — native answered and the answer was bad. Capture may still be live.
 */
export type SessionEndSweepStopOutcome =
  | 'stopped'
  | 'nothingCurrent'
  | 'notConfirmed'
  | 'error';

/**
 * What the sweep did. There is no error channel: see {@link sweepCaptureOnSessionEnd}.
 *
 * **`attempted` states ONE fact: a stop IPC was issued.** Nothing weaker. A
 * responder reading the log after a runaway has to be able to tell "the sweep
 * never got as far as acting" from "capture was told to stop and would not":
 * those are the opposite diagnosis of each other.
 *
 * So `attempted: false, reason: 'probeFailed'` now means something NARROWER than
 * it once did, and the narrowing is the point. It is reachable only under
 * `onlySession`, where a dead probe leaves no identity we are entitled to guess.
 * Under `anyActiveUpload` a dead probe no longer ends the sweep — the stop goes
 * out anyway, and the result is `{ attempted: true, basis: 'unprobed', … }`. A
 * stop issued after a failed probe is neither `probeFailed` (a stop WAS
 * attempted) nor indistinguishable from a probed stop (nothing was ever
 * established as live), and `basis` is what keeps those three apart.
 *
 * `stopInFlight` is the fourth of FR-038's four required log outcomes (ran and
 * found nothing · never ran · ran and acted · stood aside deliberately) and it
 * exists on purpose. See {@link sweepCaptureOnSessionEnd}'s `stopInFlight`
 * parameter.
 */
export type SessionEndSweepResult =
  | { attempted: false; reason: 'noActiveUpload' }
  | { attempted: false; reason: 'differentSession' }
  | { attempted: false; reason: 'stopInFlight' }
  | { attempted: false; reason: 'probeFailed'; failure: 'notConfirmed' | 'error' }
  | {
      attempted: true;
      basis: SessionEndSweepStopBasis;
      outcome: SessionEndSweepStopOutcome;
    };

/**
 * WHERE the sweep failed, and the field that keeps the two failures from reading
 * identically in Datadog.
 *
 * - `probe` — `getActive()` never gave a usable answer. No stop was issued.
 * - `stop` — native named a session, the stop IPC went out, and that failed.
 *
 * `sessionId` cannot carry this distinction, because `null` is a legitimate
 * answer on the `stop` stage too: under `anyActiveUpload` native may report
 * `{ active: true, sessionId: null }`, the sweep stops it, and the stop fails.
 * Without the stage, that line and a dead probe are byte-identical — and they
 * are opposite situations.
 */
export type SessionEndSweepFailureStage = 'probe' | 'stop';

/** Fixed literals so the fleet is searchable on each branch as one thing. */
const SWEEP_FAILED_EVENT = 'DESKTOP_RECORDING_SESSION_END_SWEEP_FAILED';
/**
 * Separate from {@link SWEEP_FAILED_EVENT}, and logged at `warn` rather than
 * `error`: a bound expiry means Rust is still converging, not that anything
 * failed. The level is chosen, not defaulted — `error` here would make the
 * ordinary case read as an incident.
 *
 * The precedent is this repo's own: `LEGACY_SWEEP_TIMEOUT_MS` in
 * `useMutateStopRecording.ts` bounds the SAME IPC at the SAME 10 000 ms and lets
 * the expiry fall into a catch whose whole body is the comment
 * "Expected on the legacy path" — silently, because there an expiry means
 * nothing. Warn is the deliberate middle: quieter than `error`, louder than that
 * silence, which FR-038 forbids here because every invocation reaching native
 * must speak.
 *
 * Cited by SYMBOL, not by line: this comment previously named
 * `useMutateStopRecording.ts:1135`, which that file's growth has since turned
 * into pause note-generation. A line number is a citation with an expiry date.
 */
const SWEEP_UNCONFIRMED_EVENT = 'DESKTOP_RECORDING_SESSION_END_SWEEP_UNCONFIRMED';
const SWEEP_STOPPED_EVENT = 'DESKTOP_RECORDING_SESSION_END_SWEEP_STOPPED';
const SWEEP_NOOP_EVENT = 'DESKTOP_RECORDING_SESSION_END_SWEEP_NOOP';
/**
 * Separate from {@link SWEEP_FAILED_EVENT}, and logged at `info`: native told us
 * there was nothing current to stop. That is the ORDINARY case — a logout with
 * no recording in progress reaches it every time — and routing it onto the
 * error event would raise a fleet alarm byte-identical to native refusing to
 * stop something live. Not suppressed: it keeps its own greppable literal, so
 * "the sweep ran and native had nothing" stays answerable from the log.
 */
const SWEEP_NOTHING_CURRENT_EVENT = 'DESKTOP_RECORDING_SESSION_END_SWEEP_NOTHING_CURRENT';

/**
 * The Rust error code for "no current epoch, or the epoch named is already
 * terminal" (`RecordingLifecycleError::StaleEpoch` and
 * `OnlineSessionError::StaleEpoch`, both mapped in
 * `src-tauri/src/ipc/error_code.rs`).
 */
const STALE_EPOCH_ERROR_CODE = 'SELF_HEALING_STALE_EPOCH';

/**
 * The `StopReason` this sweep sends. A terminal outcome that comes back
 * carrying it is one THIS stop produced; any other value is the cached reason of
 * somebody else's stop, replayed.
 *
 * Declared once and used at both the send site and the check, so the two cannot
 * drift — a replay check comparing against a reason we no longer send would
 * classify every genuine sweep stop as a replay, silently.
 */
const SWEEP_STOP_REASON = 'feFallback' satisfies StopReason;

/**
 * WHICH of the two native answers produced `outcome: 'nothingCurrent'`. Log
 * only — it changes no decision, and deliberately so: both answers mean this
 * sweep stopped nothing, which is the whole content of the outcome.
 *
 * It exists because the two are diagnosed differently. `staleEpoch` says the
 * pipeline was never ours to stop; `unattributedTerminal` says we reached a
 * recording that had already finished, which after this change is the expected
 * line for every logout following a completed recording. Folding them into one
 * literal would make a fleet spike in either invisible.
 */
type SessionEndSweepNothingCurrentVia = 'staleEpoch' | 'unattributedTerminal';

const TIMED_OUT = Symbol('sessionEndSweepTimedOut');

type Settled<T> =
  | { ok: true; value: T }
  | { ok: false; errorCode: string | null }
  | typeof TIMED_OUT;

/**
 * Read the IPC error CODE off a rejection, and nothing else.
 *
 * `errorCode` is the one field on this path that is safe to keep. It is a fixed
 * SCREAMING_SNAKE enum minted in Rust (`HasErrorCode`,
 * `src-tauri/src/ipc/error_code.rs`) from a closed set of variants — there is no
 * free text in it and nothing interpolated into it, so it cannot carry clinical
 * content or an account identifier. `message` is the opposite: it is whatever
 * string the failing library or server produced, and on this path that has
 * carried both. That is why {@link settleWithinDeadline} drops the reason
 * wholesale and this function re-reads exactly one named field rather than
 * widening the channel.
 */
function readErrorCode(reason: unknown): string | null {
  if (typeof reason !== 'object' || reason === null) return null;
  const { errorCode } = reason as { errorCode?: unknown };
  return typeof errorCode === 'string' ? errorCode : null;
}

/**
 * Race a promise against the remaining deadline without ever rejecting, and
 * without leaving a late rejection unhandled once the deadline has won.
 */
async function settleWithinDeadline<T>(
  promise: Promise<T>,
  deadlineAt: number
): Promise<Settled<T>> {
  const captured: Promise<Settled<T>> = promise.then(
    (value) => ({ ok: true as const, value }),
    // The rejection reason itself is still deliberately dropped rather than
    // logged: a library error message on this path can carry clinical text or an
    // account identifier, and a classification is all any consumer of the log
    // needs. Only `errorCode` survives, and only through {@link readErrorCode},
    // which names that one field and explains why it alone is safe. Do not widen
    // this to the message or the error object.
    (reason: unknown) => ({ ok: false as const, errorCode: readErrorCode(reason) })
  );

  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<Settled<T>>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), Math.max(0, deadlineAt - Date.now()));
  });

  try {
    return await Promise.race([captured, expiry]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Rust returns `{ active, sessionId }`; anything else is a malformed response.
 *
 * BOTH fields are checked, not just `active`. A missing or wrongly-typed
 * `sessionId` is harmless in the `onlySession` comparison — it simply fails to
 * match — but it does not stop there: it reaches the LOG PAYLOAD, where a
 * number, an object or an absent field lands on the event a responder searches
 * the fleet by. `null` is the one non-string value native legitimately sends
 * (`sessionId: string | null`), so it is accepted and nothing else is.
 */
function isWellFormed(probe: unknown): probe is ActiveOnlineChunkUpload {
  if (typeof probe !== 'object' || probe === null) return false;
  const { active, sessionId } = probe as ActiveOnlineChunkUpload;
  return (
    typeof active === 'boolean' && (typeof sessionId === 'string' || sessionId === null)
  );
}

/**
 * Ask native which session owns the active online chunk upload, and stop it when
 * this caller's targeting rule says it may.
 *
 * **This function NEVER throws and NEVER rejects.** Every internal failure —
 * `getActive()` throwing, `stopSession` rejecting, the bound expiring, a
 * malformed response — collapses into a returned
 * {@link SessionEndSweepResult}, and WHICH shape it collapses into is decided by
 * the STAGE that failed and by the TARGET. A probe that never gave a usable
 * answer (it threw, expired, or came back malformed) returns
 * `{ attempted: false, reason: 'probeFailed', failure }` **under `onlySession`
 * only**, because there no stop IPC may be addressed to an identity we could not
 * read. Under `anyActiveUpload` the same dead probe issues the stop anyway and
 * returns `{ attempted: true, basis: 'unprobed', outcome }` — see the dead-probe
 * comment in the body for why that targets the identical epoch a successful
 * probe would have named, and cannot reach a Chronicle or offline recording.
 * A caller that ignores the return value still behaves correctly. That is the
 * whole point: the sweep's safety must not depend on every call site
 * remembering to handle an error, and ending a session must never be blocked by
 * a wedged native stop.
 *
 * Targeting is answered by NATIVE, never guessed. `getRecordingStateSnapshot()`
 * is not used here on purpose — it resolves "whatever epoch is current", the
 * same resolution `current_identity()` performs, which is the race this closes.
 */
export async function sweepCaptureOnSessionEnd({
  target,
  callSite,
  stopInFlight = false,
}: {
  target: SessionEndSweepTarget;
  callSite: SessionEndSweepCallSite;
  /**
   * That the caller ALREADY has a stop in flight for this session, so the sweep
   * must stand aside.
   *
   * It exists because `online_chunk_upload_get_active` reports `active` while
   * the native session is **Stopping** as well as while it is **Active**
   * (`src-tauri/src/ipc/online_chunk_upload.rs`). The probe therefore cannot
   * tell a genuine runaway from a stop that is already draining — so without
   * this, an ordinary pause-then-End would either report "found and stopped a
   * live runaway" on the healthy path, or report an error on a stop that
   * completes fine a moment later. Both are false, and both are indistinguishable
   * in the log from a real save.
   *
   * Passed BY THE CALLER rather than read in here, for the same reason `target`
   * is: the answer lives in the caller's layer
   * (`hasOpenRustChunkUploadPauseFinalization`, `src/hooks/mutation/
   * rustChunkUploadPauseFinalization.ts`), and a headless bridge module reaching
   * up into `src/hooks` to fetch it would invert the dependency.
   *
   * It does NOT make the probe smarter about Stopping vs Active. That would be a
   * native change and is out of scope; the whole point is that the client
   * already knows.
   */
  stopInFlight?: boolean;
}): Promise<SessionEndSweepResult> {
  // Web has no native capture to outlive anything, and the bridge throws
  // outside the Tauri runtime. Returning before the probe keeps every browser
  // logout off this log stream entirely.
  if (!isTauri()) {
    return { attempted: false, reason: 'noActiveUpload' };
  }

  // Logged rather than returned silently, and this is the whole argument for
  // the branch existing in this shape: FR-038's logging requirement is that
  // EVERY outcome be distinguishable. A silent skip would not fix the misreport
  // — it would convert a wrong line into an absent one, leaving "ran and found
  // nothing", "never ran", "ran and misreported a healthy stop" and "stood
  // aside deliberately" sharing one appearance in the log. Indistinguishable
  // guard outcomes are what turn a later incident into a full investigation
  // instead of a log search; see FR-038's logging clause in
  // `specs/012-app-8723-self-healing-stop/spec.md` for the case that set it.
  if (stopInFlight) {
    logger.info(SWEEP_NOOP_EVENT, {
      callSite,
      reason: 'stopInFlight',
      // Native is deliberately not probed: it would answer `active` for the
      // draining stop and there is nothing useful to report from it.
      sessionId: target.kind === 'onlySession' ? target.sessionId : null,
    });
    return { attempted: false, reason: 'stopInFlight' };
  }

  const deadlineAt = Date.now() + SESSION_END_SWEEP_TIMEOUT_MS;

  const probe = await settleWithinDeadline(
    onlineChunkUploadBridge.getActive(),
    deadlineAt
  );

  if (probe === TIMED_OUT || !probe.ok || !isWellFormed(probe.value)) {
    const probeFailure = probe === TIMED_OUT ? 'notConfirmed' : 'error';

    // A DEAD PROBE MUST NOT MEAN A DEAD SWEEP under `anyActiveUpload`. Standing
    // aside here is the failure mode the sweep exists to prevent: the probe is
    // exactly the call that wedges when native is unwell, and that is precisely
    // when a runaway upload is most likely to be running.
    //
    // Issuing the stop blind is safe under this target, and not by assumption:
    //
    //  - It targets THE IDENTICAL EPOCH a successful probe would have named.
    //    `online_chunk_upload_stop` resolves identity from its params and falls
    //    through to `lifecycle.current_identity()`, and this bridge never sends
    //    `recordingId`/`generation` — so "whatever is current" is the target
    //    either way. Not a wider target: the same one.
    //  - It cannot hit something a probe would have spared. EVERY production
    //    caller of `claim_at_start` is on the ONLINE-CHUNK path, so
    //    `current_identity()` can only ever name an online-chunk-upload
    //    recording — never a Chronicle device session, never an offline capture.
    //    There are three, and the claim is about the path rather than the count
    //    so a fourth on the same path does not rot it:
    //    `src-tauri/src/ipc/online_chunk_upload.rs:234` (the start command),
    //    `src-tauri/src/session/online_chunk_stream.rs:1429` and
    //    `src-tauri/src/session/online_chunk_upload.rs:947` (the two chunk-upload
    //    pipelines). An earlier revision of this comment said "one production
    //    caller"; the conclusion survives, the count did not.
    //  - The guards that protect this path survive. `stopInFlight` is checked
    //    BEFORE the probe, so it still gates; `differentSession` does not exist
    //    for `anyActiveUpload` by construction.
    //  - If nothing is in fact current, Rust answers `SELF_HEALING_STALE_EPOCH`
    //    and touches nothing. That is `outcome: 'nothingCurrent'`, not an error.
    //
    // `onlySession` STAYS A NO-OP, and that asymmetry is the whole safety
    // argument. There the caller's entitlement is "this session and no other",
    // and a blind stop would resolve to whatever is current — which on a shared
    // desktop can be another patient's live recording. An identity we failed to
    // read is not an identity we may guess.
    if (target.kind === 'anyActiveUpload') {
      return issueStop({
        callSite,
        // Native named nothing, and saying so is more useful than echoing a
        // session the probe never confirmed.
        sessionId: null,
        basis: 'unprobed',
        probeFailure,
        deadlineAt,
      });
    }

    // `sessionId: null` is not a gap in the log line — native named nothing, and
    // saying so is more useful than echoing the session the caller was ending.
    // `stage: 'probe'` is what makes it readable: no stop was issued here, and
    // `sessionId: null` alone cannot say so because the stop branch can produce
    // it too.
    return reportProbeFailure({ callSite, failure: probeFailure });
  }

  const nativeSessionId = probe.value.sessionId;

  if (!probe.value.active) {
    logger.info(SWEEP_NOOP_EVENT, {
      callSite,
      reason: 'noActiveUpload',
      sessionId: nativeSessionId,
    });
    return { attempted: false, reason: 'noActiveUpload' };
  }

  // The criterion that matters most. Under `onlySession` a mismatch — including
  // a null id native could not attribute — is a no-op, because the recording
  // running is somebody else's session and, on a shared desktop, another
  // patient's audio.
  if (target.kind === 'onlySession' && nativeSessionId !== target.sessionId) {
    logger.info(SWEEP_NOOP_EVENT, {
      callSite,
      reason: 'differentSession',
      sessionId: nativeSessionId,
    });
    return { attempted: false, reason: 'differentSession' };
  }

  return issueStop({
    callSite,
    sessionId: nativeSessionId,
    basis: 'probed',
    probeFailure: null,
    deadlineAt,
  });
}

/**
 * Issue the stop IPC, bound it, and classify what came back.
 *
 * Extracted so the probed and unprobed paths cannot drift: the stop, the bound,
 * the four outcomes and the four log levels are written once. `basis` and
 * `probeFailure` are the only things the two callers differ by, and both reach
 * the log line rather than being inferred from it.
 */
async function issueStop({
  callSite,
  sessionId,
  basis,
  probeFailure,
  deadlineAt,
}: {
  callSite: SessionEndSweepCallSite;
  sessionId: string | null;
  basis: SessionEndSweepStopBasis;
  /** The probe failure this stop was issued in spite of, or `null` if none. */
  probeFailure: 'notConfirmed' | 'error' | null;
  deadlineAt: number;
}): Promise<SessionEndSweepResult> {
  // NOTE ON THE DEADLINE, deliberately with NO FLOOR. When the probe has already
  // spent the whole bound, `settleWithinDeadline` gives this stop zero remaining
  // time — but the IPC is still ISSUED: `stopSession()` is called, and the
  // invoke goes out, before the race it loses. Rust then keeps converging past
  // the bound exactly as `SESSION_END_SWEEP_TIMEOUT_MS` documents, and the
  // client honestly reports `notConfirmed`. A floor would extend the hold on the
  // clinician's session end past the bound we promised, to buy a confirmation
  // nobody is waiting for.
  const stopped = await settleWithinDeadline(
    // `feFallback` is an existing, non-note-generating `StopReason`: the FE, not
    // the ordinary stop path, is issuing this terminal. The recording being
    // swept is by definition not the one the user's note should be built from,
    // so FR-022/FR-024a's "only a final UserStop generates" must hold here.
    //
    // It is ALSO the evidence this stop ran at all — see the `Ok` branch below.
    onlineChunkUploadBridge.stopSession({ reason: SWEEP_STOP_REASON }),
    deadlineAt
  );

  const context = {
    callSite,
    sessionId,
    stage: 'stop' satisfies SessionEndSweepFailureStage,
    attempted: true,
    basis,
    ...(probeFailure === null ? {} : { probeFailure }),
  };

  if (stopped === TIMED_OUT) {
    logger.warn(SWEEP_UNCONFIRMED_EVENT, {
      ...context,
      outcome: 'notConfirmed',
      failure: 'notConfirmed',
      boundMs: SESSION_END_SWEEP_TIMEOUT_MS,
    });
    return { attempted: true, basis, outcome: 'notConfirmed' };
  }

  if (!stopped.ok) {
    // "There was nothing to stop" is not "native refused to stop something
    // live". Without this branch the ordinary case — a logout with nothing
    // recording and a flaky probe — raises an ERROR-level fleet alarm identical
    // to the one a real refusal raises, and an alarm that cannot tell them apart
    // teaches whoever reads it to ignore both.
    if (stopped.errorCode === STALE_EPOCH_ERROR_CODE) {
      logger.info(SWEEP_NOTHING_CURRENT_EVENT, {
        ...context,
        outcome: 'nothingCurrent',
        nothingCurrentVia: 'staleEpoch' satisfies SessionEndSweepNothingCurrentVia,
        errorCode: stopped.errorCode,
      });
      return { attempted: true, basis, outcome: 'nothingCurrent' };
    }
    logger.error(SWEEP_FAILED_EVENT, {
      ...context,
      outcome: 'error',
      failure: 'error',
      // Safe by construction — a closed Rust enum, never free text. See
      // {@link readErrorCode}. `null` when the rejection carried no code.
      errorCode: stopped.errorCode,
    });
    return { attempted: true, basis, outcome: 'error' };
  }

  // `Ok` IS NOT PROOF THAT WE STOPPED ANYTHING, and reading it as proof is the
  // defect this branch exists to close.
  //
  // `terminal::execute` replays an already-terminal epoch's CACHED outcome as
  // `Ok` (`src-tauri/src/recording_lifecycle/terminal.rs`, step 1 — pinned by
  // `execute_idempotent_replays_cached_outcome_without_touching_pipelines` in
  // that file), re-running no pipelines and stopping nothing. And nothing ever
  // clears the epoch: `mark_terminal` moves the phase, `current_identity()` has
  // no phase filter, and `epoch` is only ever assigned. So after ANY completed
  // recording in this app run, the blind stop this sweep issues resolves to that
  // finished epoch and replays it — the single most ordinary sequence in the
  // app (record · stop · log out) used to log STOPPED for a stop that stopped
  // nothing.
  //
  // The discriminator was always on the wire and was being discarded at the type
  // boundary. A stop that genuinely ran carries back the reason WE sent;
  // `outcome_from_stop_result` copies the argument onto the outcome verbatim, so
  // `feFallback` here means this call produced the terminal. A replay carries
  // the ORIGINAL stop's reason, typically `userStop`.
  //
  // The comparison deliberately catches an ABSENT `reason` — and an absent
  // PAYLOAD — too, which is why it is read through `?.` rather than trusting the
  // declared type. `StopOnlineChunkUploadResult` is an assertion about the wire,
  // not a proof of it: Rust declares `reason` non-optional on `TerminalOutcome`,
  // so a response missing it did not come from the Rust we compile against, and
  // "we cannot tell" must not resolve to `stopped` — that is the whole shape of
  // the bug. It resolves to `nothingCurrent`, and `replayedReason: null` says so
  // in the log.
  //
  // KNOWN RESIDUAL: a SECOND sweep in one app run replaying a FIRST sweep's own
  // `feFallback` terminal is indistinguishable here, because the reason matches.
  // It is not closed by comparing `recordingId`/`generation` either — this stop
  // sends no identity and the client holds no expected epoch to compare against.
  // What the log carries instead is the epoch that was reached, so two sweep
  // lines naming the same `recordingId` + `generation` are the field signature
  // of that case. Closing it properly is the Rust change (`current_identity()`
  // skipping a Terminal epoch), which is a separate ticket.
  if (stopped.value?.reason !== SWEEP_STOP_REASON) {
    logger.info(SWEEP_NOTHING_CURRENT_EVENT, {
      ...context,
      outcome: 'nothingCurrent',
      nothingCurrentVia:
        'unattributedTerminal' satisfies SessionEndSweepNothingCurrentVia,
      // A closed Rust enum, never free text (FR-025), or `null` when the field
      // did not arrive at all.
      replayedReason: stopped.value?.reason ?? null,
      // Opaque native identifiers, no patient or clinical content. They decide
      // nothing here — see the doc on `StopOnlineChunkUploadResult.recordingId`.
      recordingId: stopped.value?.recordingId ?? null,
      generation: stopped.value?.generation ?? null,
    });
    return { attempted: true, basis, outcome: 'nothingCurrent' };
  }

  logger.info(SWEEP_STOPPED_EVENT, {
    ...context,
    outcome: 'stopped',
    recordingId: stopped.value.recordingId,
    generation: stopped.value.generation,
  });
  return { attempted: true, basis, outcome: 'stopped' };
}

/**
 * The PROBE failure log line — the one branch where no stop IPC was issued.
 *
 * It is reachable only under `onlySession`. Under `anyActiveUpload` a dead probe
 * no longer ends the sweep: the stop goes out blind and {@link issueStop} owns
 * the line. That asymmetry is the point — see the dead-probe comment in
 * {@link sweepCaptureOnSessionEnd}.
 *
 * TWO events at TWO levels, because the two failure KINDS do not mean the same
 * thing:
 *
 * - `error` — native answered and the answer was bad (`getActive` threw, or came
 *   back malformed). Something is wrong.
 * - `notConfirmed` — the bound expired. All that is true is that we stopped
 *   watching. See {@link SWEEP_UNCONFIRMED_EVENT} for the precedent that sets
 *   this level.
 *
 * `stage: 'probe'` is what keeps it from reading like a refused stop, and
 * `attempted: false` follows from it: FR-038 requires the failure branch to
 * carry whether a stop WAS attempted, and here none was. `sessionId` cannot
 * carry that distinction, because `null` is a legitimate answer on the stop
 * stage too — under `anyActiveUpload` native may report
 * `{ active: true, sessionId: null }`, the sweep stops it, and the stop fails.
 * See {@link SessionEndSweepFailureStage}.
 *
 * Every line carries only: the fixed event name, the session id NATIVE named
 * (here always `null` — it named none), which call site ran, the stage, whether
 * a stop was attempted, the failure kind, and — on the bound branch — the bound
 * that expired. No patient identifier, no note or transcript content, no audio
 * path, no raw error.
 */
function reportProbeFailure({
  callSite,
  failure,
}: {
  callSite: SessionEndSweepCallSite;
  failure: 'notConfirmed' | 'error';
}): SessionEndSweepResult {
  const line = {
    callSite,
    sessionId: null,
    stage: 'probe' satisfies SessionEndSweepFailureStage,
    attempted: false,
    failure,
  };
  if (failure === 'notConfirmed') {
    logger.warn(SWEEP_UNCONFIRMED_EVENT, {
      ...line,
      boundMs: SESSION_END_SWEEP_TIMEOUT_MS,
    });
  } else {
    logger.error(SWEEP_FAILED_EVENT, line);
  }
  return { attempted: false, reason: 'probeFailed', failure };
}
